import React from 'react';
import styled from '@2pulse/responsive';
import { DateTime } from 'luxon';
import { FilterButton } from '@app/components';
import { normalize } from '@app/helpers';
import { theme } from '@app/theme';
import type { FiscalArchiveStatus, IFiscalArchiveSummary } from '@tickeat/types';
import { COULEUR_ETAT } from './couleurs';
import { actionsDArchive, libelleArchive, libelleEtatArchive, tailleEnMo } from './fiscal.logic';

interface Props {
  archive: IFiscalArchiveSummary;
  peutGerer: boolean;
  onTelecharger: (a: IFiscalArchiveSummary) => void;
  onVerifier: (a: IFiscalArchiveSummary) => void;
  onRelancer: (a: IFiscalArchiveSummary) => void;
  // La copie de l'empreinte a besoin d'un toast, que cette carte n'a pas :
  // la colonne le fournit. Absente, le bouton ne se rend pas.
  onCopierEmpreinte?: (a: IFiscalArchiveSummary) => void;
  // Action en cours sur CETTE archive, pour n'animer que son bouton.
  enCours: 'telecharger' | 'verifier' | 'relancer' | null;
}

// Générée = verte, en attente = grise, échec = rouge. Même vocabulaire de
// couleurs que les états de caisse (`couleurs.ts`).
const COULEUR_ARCHIVE: Record<FiscalArchiveStatus, string> = {
  GENEREE: COULEUR_ETAT.vert,
  EN_ATTENTE: COULEUR_ETAT.gris,
  ECHEC: COULEUR_ETAT.rouge,
};

const dateFr = (iso: string, avecHeure = true) => DateTime.fromISO(iso).toFormat(avecHeure ? 'dd/MM/yyyy HH:mm' : 'dd/MM/yyyy');

// 6.11.4.3 (contre-audit E-22) : le contrôle d'intégrité additionnel de l'engine doit
// conserver l'enregistrement de ses erreurs — cette ligne en est la restitution, qu'il ait
// trouvé un défaut ou non. Même formulation que sur la fiche de caisse (`FiscalRegisterCard`).
const libelleDerniereVerification = (v: NonNullable<IFiscalArchiveSummary['lastVerification']>): string => {
  const base = `Dernière vérification : le ${dateFr(v.at)}, par ${v.by}`;
  return v.ok ? `${base}, OK` : `${base} — ${v.defect?.reason ?? 'défaut non qualifié'}`;
};

// Fix round 1, I1 : une vérification VERTE n'efface jamais le dernier DÉFAUT constaté — seule
// une vérification ROUGE ultérieure le remplace. Restitué à part de « Dernière vérification »,
// qui peut donc être verte alors qu'un défaut reste visible ici.
const libelleDernierDefaut = (d: NonNullable<IFiscalArchiveSummary['lastDefect']>): string =>
  `Dernier défaut constaté le ${dateFr(d.at)}, par ${d.by} : ${d.reason}`;

// SA-17 (contre-audit, 6.9.1.4) : une archive FINALE (rebut, abandon, terminal
// disparu) ne clôture JAMAIS d'exercice — la caisse ne clôturera plus. Même
// texte, mot pour mot, que `MENTION_ARCHIVE_FINALE` (`@common/fiscal-export`,
// engine) et que l'écran Conformité de l'iPad (`compliance-actions.ts`) :
// trois restitutions, une seule chaîne.
const MENTION_ARCHIVE_FINALE =
  "La caisse n'a pas clôturé l'exercice : les données sont celles reçues par le serveur central au moment de la constitution.";

export const FiscalArchiveCard = ({ archive, peutGerer, onTelecharger, onVerifier, onRelancer, onCopierEmpreinte, enCours }: Props) => {
  const actions = actionsDArchive(archive, peutGerer);

  return (
    <Carte>
      <EnTete>
        <EnTeteGauche>
          <Pastille couleur={COULEUR_ARCHIVE[archive.status]}>{libelleEtatArchive(archive.status)}</Pastille>
          <Titre testID="titre-archive">{libelleArchive(archive)}</Titre>
        </EnTeteGauche>
        {archive.constitutedIso && <DateTexte>constituée le {dateFr(archive.constitutedIso)}</DateTexte>}
      </EnTete>

      <Grille>
        <Colonne>
          <Cle>Taille</Cle>
          <Valeur testID="taille-archive">{tailleEnMo(archive.sizeBytes)}</Valeur>
        </Colonne>

        {archive.sha256 && (
          <Colonne>
            <Cle>Empreinte SHA-256</Cle>
            <LigneValeur>
              <Empreinte selectable testID="sha-archive">{archive.sha256.slice(0, 16)}…</Empreinte>
              {onCopierEmpreinte && <FilterButton title="Copier" onPress={() => onCopierEmpreinte(archive)} testID="copier-sha-archive" />}
            </LigneValeur>
          </Colonne>
        )}

        {archive.acknowledgedByRegisterAt && (
          <Colonne>
            <Cle>Acquittée par la caisse</Cle>
            <Valeur testID="acquittee-archive">
              le {dateFr(archive.acknowledgedByRegisterAt, false)}
              {archive.acknowledgedJetSequence !== null ? ` — JET 30 n° ${archive.acknowledgedJetSequence}` : ''}
            </Valeur>
          </Colonne>
        )}
      </Grille>

      {archive.lastVerification && (
        <Verification ok={archive.lastVerification.ok} testID="derniere-verification-archive">
          {libelleDerniereVerification(archive.lastVerification)}
        </Verification>
      )}

      {archive.lastDefect && (
        <Verification ok={false} testID="dernier-defaut-archive">
          {libelleDernierDefaut(archive.lastDefect)}
        </Verification>
      )}

      {archive.failure && (
        <Echec testID="echec-archive">
          Échec {archive.failure.code}{archive.failure.chain ? ` (${archive.failure.chain}${archive.failure.sequence !== undefined ? ` n° ${archive.failure.sequence}` : ''})` : ''} : {archive.failure.detail}
        </Echec>
      )}

      {archive.kind === 'FINALE' && <Mention testID="mention-archive-finale">{MENTION_ARCHIVE_FINALE}</Mention>}

      {(actions.telecharger || actions.verifier || actions.relancer) && (
        <Actions>
          {actions.telecharger && <FilterButton title="Télécharger" loading={enCours === 'telecharger'} onPress={() => onTelecharger(archive)} testID="telecharger-archive" />}
          {actions.verifier && <FilterButton title="Vérifier" loading={enCours === 'verifier'} onPress={() => onVerifier(archive)} testID="verifier-archive" />}
          {actions.relancer && <FilterButton title="Relancer" loading={enCours === 'relancer'} onPress={() => onRelancer(archive)} testID="relancer-archive" />}
        </Actions>
      )}
    </Carte>
  );
};

// Même carte que `OrderCardContainer` : fond translucide, coins très arrondis.
const Carte = styled.View(({ theme }) => ({
  width: '100%',
  backgroundColor: theme.colors.translucid,
  borderRadius: normalize(42),
  padding: normalize(21),
  marginBottom: normalize(13),
  rowGap: normalize(13),
}));

const EnTete = styled.View({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  columnGap: normalize(13),
  flexWrap: 'wrap',
});

const EnTeteGauche = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
  columnGap: normalize(13),
  flexShrink: 1,
});

const Pastille = styled.Text<{ couleur: string }>(({ theme, props }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(14),
  fontWeight: '700',
  color: theme.colors.babypowder,
  backgroundColor: props.couleur,
  borderRadius: normalize(10),
  paddingHorizontal: normalize(12),
  paddingVertical: normalize(4),
  overflow: 'hidden',
}));

const Titre = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(16),
  fontWeight: '700',
  color: theme.colors.babypowder,
}));

const DateTexte = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(14),
  color: theme.colors.grey,
}));

const Grille = styled.View({
  flexDirection: 'row',
  flexWrap: 'wrap',
  columnGap: normalize(34),
  rowGap: normalize(13),
});

const Colonne = styled.View({ rowGap: normalize(3) });

const Cle = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(14),
  fontWeight: '700',
  color: theme.colors.celadonblue,
}));

const Valeur = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(16),
  color: theme.colors.babypowder,
}));

const LigneValeur = styled.View({
  flexDirection: 'row',
  alignItems: 'center',
  columnGap: normalize(10),
});

const Empreinte = styled.Text(({ theme }) => ({
  fontFamily: 'monospace',
  fontSize: normalize(14),
  color: theme.colors.babypowder,
  opacity: 0.7,
}));

const Echec = styled.Text({
  fontFamily: 'Work Sans',
  fontSize: normalize(14),
  lineHeight: normalize(20),
  color: theme.colors.crimson,
});

const Verification = styled.Text<{ ok: boolean }>(({ theme, props }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(14),
  lineHeight: normalize(20),
  color: props.ok ? theme.colors.grey : theme.colors.crimson,
}));

const Mention = styled.Text(({ theme }) => ({
  fontFamily: 'Work Sans',
  fontSize: normalize(14),
  lineHeight: normalize(20),
  fontStyle: 'italic',
  color: theme.colors.grey,
}));

const Actions = styled.View({
  flexDirection: 'row',
  columnGap: normalize(10),
  rowGap: normalize(10),
  flexWrap: 'wrap',
  justifyContent: 'flex-end',
});
